import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { auth } from '../../firebase'; // Import the auth instance from your firebase.js
import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, 
    GoogleAuthProvider, 
    signInWithPopup 
} from "firebase/auth";
import axios from 'axios';
import { API_URL, SOCKET_URL } from '../../apiConfig';

const PublicAuth = ({ setAuthenticated, setUser, setUserType }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // --- DEBUGGING STEP ---
    // This will run when the component first loads.
    useEffect(() => {
        console.log("Auth object from Firebase:", auth);
        if (!auth || !auth.app) {
            console.error("Firebase auth object is not valid. Check your firebase.js file and its export.");
            setError("Firebase is not configured correctly. Please check the console.");
        }
    }, []);

    const syncUserWithBackend = async (firebaseUser) => {
        try {
            const idToken = await firebaseUser.getIdToken();
            const response = await axios.post(
                `${API_URL}/users/auth`, 
                { 
                    role: 'volunteer',
                    name: firebaseUser.displayName || name,
                },
                {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                }
            );
            return response.data;
        } catch (err) {
            console.error("Error syncing user with backend:", err);
            setError("Could not sync your profile with our server.");
            return null;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!auth || !auth.app) {
            setError("Firebase is not initialized. Cannot proceed with authentication.");
            setLoading(false);
            return;
        }

        if (!isLogin) { // Handle Sign Up
            if (password !== confirmPassword) {
                setError('Passwords do not match.');
                setLoading(false);
                return;
            }
            try {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const backendUser = await syncUserWithBackend(userCredential.user);
                if (backendUser) {
                    setUser(backendUser);
                    setUserType('volunteer');
                    setAuthenticated(true);
                }
            } catch (err) {
                setError(err.message);
            }
        } else { // Handle Login
            try {
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                const backendUser = await syncUserWithBackend(userCredential.user);
                if (backendUser) {
                    setUser(backendUser);
                    setUserType('volunteer');
                    setAuthenticated(true);
                }
            } catch (err) {
                setError(err.message);
            }
        }
        setLoading(false);
    };

    const handleGoogleLogin = async () => {
        setLoading(true);
        setError('');
        if (!auth || !auth.app) {
            setError("Firebase is not initialized. Cannot proceed with authentication.");
            setLoading(false);
            return;
        }
        const provider = new GoogleAuthProvider();
        try {
            const result = await signInWithPopup(auth, provider);
            const backendUser = await syncUserWithBackend(result.user);
            if (backendUser) {
                setUser(backendUser);
                setUserType('volunteer');
                setAuthenticated(true);
            }
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    };

    return (
        <motion.div
            key="public"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
        >
            <h3 className="text-xl font-semibold text-center text-gray-700 mb-6">
                {isLogin ? 'Volunteer Login' : 'Volunteer Sign Up'}
            </h3>

            {error && (
                <div className="p-3 bg-red-100 text-red-700 rounded-lg mb-4 text-sm">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Full Name</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-800 transition" required />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-800 transition" required />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Password</label>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-800 transition" required />
                </div>

                {!isLogin && (
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">Confirm Password</label>
                        <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-800 transition" required />
                    </div>
                )}

                <button type="submit" disabled={loading} className="w-full py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all disabled:bg-gray-400 flex items-center justify-center">
                    {loading ? 'Processing...' : isLogin ? 'Login' : 'Sign Up'}
                </button>
            </form>

            <div className="my-6 flex items-center">
                <div className="flex-grow border-t border-gray-300"></div>
                <span className="flex-shrink mx-4 text-gray-400 text-sm">Or</span>
                <div className="flex-grow border-t border-gray-300"></div>
            </div>

            <button onClick={handleGoogleLogin} disabled={loading} className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-all disabled:bg-gray-200">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.545 10.239v3.821h5.445c-0.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866 0.549 3.921 1.453l2.814-2.814c-1.796-1.672-4.177-2.698-6.735-2.698-5.522 0-10 4.477-10 10s4.478 10 10 10c8.396 0 10-7.496 10-10 0-0.671-0.068-1.325-0.182-1.977h-9.818z"/>
                </svg>
                <span>Continue with Google</span>
            </button>

            <div className="text-center mt-6">
                <button onClick={() => setIsLogin(!isLogin)} className="text-sm text-gray-600 hover:text-black hover:underline">
                    {isLogin ? 'Need an account? Sign Up' : 'Already have an account? Login'}
                </button>
            </div>
        </motion.div>
    );
};

export default PublicAuth;
